import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowRight, Mail } from "lucide-react";
import Eyebrow from "./Eyebrow";
import ErrorBoundary from "./ErrorBoundary";
import { Marquee } from "@/components/shadcn-space/animations/marquee";

const IdCard = lazy(() => import("./ui/id-card"));

const HERO_COMPANIES = [
  { name: "isolutions", logo: "/images/logos/isolutions.webp", imgClass: "h-4 sm:h-5" },
  { name: "Rega", logo: "/images/logos/rega.webp" },
  { name: "CSL Behring", logo: "/images/logos/csl-behring.webp" },
  { name: "Farner / Rod", logo: "/images/logos/farner-rod.webp" },
  { name: "Aquila", logo: "/images/logos/aquila.webp", imgClass: "h-7 sm:h-8" },
  { name: "Helion", logo: "/images/logos/helion.webp" },
  { name: "Apple", logo: "/images/logos/apple.webp" },
  { name: "Swisscom", logo: "/images/logos/swisscom.webp", imgClass: "h-9 sm:h-10" },
];

const HeroPhotoFallback = () => (
  <img
    src="/images/hero.webp"
    alt="Andreas Fischinger – Kundensupport & Microsoft 365, Zürich"
    className="w-full h-full object-cover object-[center_15%]"
    fetchPriority="high"
    loading="eager"
    decoding="sync"
  />
);

// The 3D badge is desktop-only and heavy (GLB + WebGL) — gate the mount on a real
// matchMedia check so it never even starts loading on mobile (CSS `hidden` alone
// still mounts/fetches it).
const useIsDesktop = () => {
  // Starts false to match the prerendered HTML; the real value is read after hydration.
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = () => setIsDesktop(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isDesktop;
};

const HeroSection = () => {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const isDesktop = useIsDesktop();
  const [badgeFailed, setBadgeFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const retryCountRef = useRef(0);

  // A single context loss can be transient — remount once with a fresh Canvas/WebGL context
  // before giving up permanently and falling back to the static photo.
  const handleContextLost = () => {
    if (retryCountRef.current < 1) {
      retryCountRef.current += 1;
      setRetryKey((k) => k + 1);
    } else {
      setBadgeFailed(true);
    }
  };

  return (
    <section className="relative border-b">
      {/* Desktop portrait — the 3D badge is the only visual here (no static photo behind it);
          falls back to the static photo only if the badge genuinely fails to load/render. */}
      <div className="absolute right-0 top-0 bottom-0 w-[44%] hidden lg:block overflow-hidden">
        {isDesktop && !badgeFailed ? (
          <ErrorBoundary onError={() => setBadgeFailed(true)}>
            <Suspense fallback={null}>
              <IdCard
                key={retryKey}
                frontImage="/images/hero.webp"
                data-testid="hero-lanyard-card"
                className="w-full h-full"
                onContextLost={handleContextLost}
              />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <HeroPhotoFallback />
        )}
      </div>

      {/* Text block */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="lg:max-w-[56%] py-20 sm:py-24 lg:py-32 space-y-6">

          {/* Available badge */}
          <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm font-medium shadow-sm text-green-600 dark:text-green-400">
            <span className="relative inline-flex size-2">
              <span className="animate-ping-slow absolute inset-0 rounded-full bg-green-500/50" />
              <span className="relative inline-flex size-2 rounded-full bg-green-500 dark:bg-green-400" />
            </span>
            Verfügbar ab Oktober 2026
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-[4rem] font-bold tracking-tight leading-[1.08] text-foreground"
          >
            Hallo, mein Name ist Andreas 👋
          </h1>

          {/* Sub-title */}
          <p className="text-muted-foreground text-xl sm:text-2xl font-medium">
            Kundensupport & Customer Experience · Microsoft 365
          </p>

          {/* Description */}
          <p className="text-base text-foreground/70 max-w-xl leading-relaxed">
            Support-Spezialist aus Zürich. Mir geht es nicht nur um das gelöste
            Ticket, sondern um das ganze Erlebnis: hochstehender Support auf
            Deutsch und Englisch, eine durchdachte Customer Journey und breite
            Erfahrung in der Microsoft-365-Administration, in Bankenwesen, Energie
            und Pharma.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("kontakt")}
              className="group inline-flex items-center gap-2.5 rounded-full border bg-card
                         px-5 py-2.5 text-base font-medium shadow-sm
                         hover:text-accent hover:border-accent/30 transition-colors"
            >
              <span className="bg-accent relative flex size-2.5 items-center justify-center
                               overflow-hidden rounded-full transition-all duration-300
                               group-hover:size-6">
                <ArrowRight className="text-accent-foreground absolute size-4 -translate-x-4
                                       opacity-0 transition-all duration-300
                                       group-hover:translate-x-0 group-hover:opacity-100" />
              </span>
              Unterlagen anfordern
            </button>

            <a
              href="mailto:andreas@fischinger.ch"
              data-testid="hero-cta-email"
              className="inline-flex items-center gap-2 rounded-full border bg-card
                         px-5 py-2.5 text-base font-medium shadow-sm
                         hover:text-accent hover:border-accent/30 transition-colors"
            >
              <Mail className="size-4" strokeWidth={1.8} />
              andreas@fischinger.ch
            </a>
          </div>

          {/* Companies worked with — kept above the fold */}
          <div className="pt-6 space-y-3" data-testid="hero-company-logos">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
              Erfahrung bei
            </p>
            {/* Endless logo band, same Marquee as the Werkzeugkasten */}
            <div className="relative overflow-hidden">
              <Marquee pauseOnHover className="p-0 py-1 [--duration:35s] [--gap:0.75rem]">
                {HERO_COMPANIES.map(({ name, logo, imgClass }) => (
                  <div
                    key={name}
                    data-testid={`hero-company-logo-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex h-11 shrink-0 items-center justify-center rounded-lg border bg-white px-3.5 shadow-sm"
                    title={name}
                  >
                    <img
                      src={logo}
                      alt={name}
                      className={`w-auto object-contain ${imgClass || "h-5 sm:h-6"}`}
                      loading="eager"
                    />
                  </div>
                ))}
              </Marquee>

              {/* Left fade */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-10 sm:w-16 bg-gradient-to-r from-background to-transparent" />
              {/* Right fade */}
              <div className="pointer-events-none absolute inset-y-0 right-0 w-10 sm:w-16 bg-gradient-to-l from-background to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile portrait strip */}
      <div className="lg:hidden w-full h-80 overflow-hidden relative -mt-6">
        <img
          src="/images/hero.webp"
          alt="Andreas Fischinger"
          className="w-full h-full object-cover object-[center_35%]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(39,22%,96%)] dark:from-[hsl(0,0%,9%)] via-transparent to-[hsl(39,22%,96%)] dark:to-[hsl(0,0%,9%)] pointer-events-none" />
      </div>
    </section>
  );
};

export default HeroSection;
